import { useI18n } from '../../../i18n';
// PromotionDetailModal — read-only view of all promotion fields

import { useEffect } from 'react';
import { usePromotion } from '../../../hooks';

interface Props {
  id: string;
  onClose: () => void;
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800 break-all">{value || '—'}</p>
    </div>
  );
}

function TagList({ label, items, emptyLabel }: { label: string; items?: any; emptyLabel: string }) {
  const isArray = Array.isArray(items);
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      {!isArray || items.length === 0 ? (
        // Scope rỗng = áp dụng TẤT CẢ (xác nhận với BE: orders/services.py verify_order_items)
        <span className="mt-1 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
          {emptyLabel}
        </span>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1">
          {items.map((v: any, i: number) => {
            const key = typeof v === 'object' && v ? (v.id || i) : String(v);
            const text = typeof v === 'object' && v ? (v.label || v.name || v.id) : String(v);
            return (
              <span key={String(key)} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 break-all">
                {String(text)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDate(iso?: string | null) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return String(iso);
  }
}

export function PromotionDetailModal({ id, onClose }: Props) {
  const { t } = useI18n();
  const { data: raw, isLoading, error: queryError } = usePromotion(id);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Safe wrapper render to prevent any React crash from making the screen go blank
  try {
    const p = raw ? ((raw as any).data || raw) : null;

    // Dữ liệu áp dụng có thể nằm trong p.applicable_to hoặc p.product_ids/category_ids/store_ids
    const mapItems = (arr: any) => {
      if (!Array.isArray(arr)) return [];
      return arr.map(item => {
        if (typeof item === 'object' && item !== null) {
          // backend trả về cấu trúc dạng { product_name: "Tên", product: "ID" } hoặc { name: "Tên", id: "ID" }
          const name = item.product_name || item.category_name || item.store_name || item.name || item.label || item.id;
          return { id: item.id, name: name };
        }
        return { id: String(item), name: String(item) };
      });
    };
    const productItems = mapItems(p?.applicable_to?.products || p?.product_scopes || p?.product_ids);
    const categoryItems = mapItems(p?.applicable_to?.categories || p?.category_scopes || p?.category_ids);
    const storeItems = mapItems(p?.applicable_to?.stores || p?.store_scopes || p?.store_ids);

    const nameVal = p ? String(p.name || '') : '';
    const codeVal = p ? String(p.code || '') : '';
    // Bug #147: `type` của promotion về tới đây là hằng số của BE
    // ("PERCENTAGE" / "FIXED_AMOUNT") — in thẳng thì đọc như tên biến. Đổi sang
    // đúng nhãn đang dùng ở bảng và ở thẻ thống kê; giá trị nào không nhận ra
    // (vd `promotion_type` = COUPON/LOYALTY) thì giữ nguyên như cũ.
    const rawTypeVal = p ? String(p.promotion_type || p.type || '') : '';
    const discountTypeLabels: Record<string, string> = {
      PERCENTAGE: t('percentage'),
      PERCENT: t('percentage'),
      FIXED_AMOUNT: t('fixedAmount'),
      FIXED: t('fixedAmount'),
    };
    const typeVal = discountTypeLabels[rawTypeVal.toUpperCase()] ?? rawTypeVal;
    const valueVal = p ? (p.discount_value != null ? String(p.discount_value) : (p.value != null ? String(p.value) : '')) : '';
    const getCondValue = () => {
      if (!p) return '';
      let rawCond = p.conditions || p.description || '';
      if (rawCond === '[object Object]' && p.conditions && typeof p.conditions === 'object') {
        rawCond = p.conditions;
      }
      if (typeof rawCond === 'object' && rawCond !== null) {
        // Parse the conditions object into friendly key-value strings.
        // Object rỗng -> trả '' để `Field` hiện '—' như mọi ô trống khác, thay
        // vì câu "No conditions" viết cứng tiếng Anh (lọt qua i18n).
        const entries = Object.entries(rawCond).map(([key, val]) => {
          const friendlyKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const friendlyVal = typeof val === 'number' ? val.toLocaleString() : String(val);
          return `${friendlyKey}: ${friendlyVal}`;
        });
        return entries.join(', ');
      }
      try {
        const parsed = JSON.parse(String(rawCond));
        if (typeof parsed === 'object' && parsed !== null) {
          const entries = Object.entries(parsed).map(([key, val]) => {
            const friendlyKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const friendlyVal = typeof val === 'number' ? val.toLocaleString() : String(val);
            return `${friendlyKey}: ${friendlyVal}`;
          });
          return entries.join(', ');
        }
      } catch {
        // Not a JSON string, fallback to original string representation
      }
      return String(rawCond);
    };
    const condVal = getCondValue();
    // Chỉ `start_at`/`end_at` — xem ghi chú ở PromotionsPage (BE 19/08/2026).
    const startVal = p ? formatDate(p.start_at) : '';
    const endVal = p ? formatDate(p.end_at) : '';
    const statusVal = p ? String(p.status || '') : '';

    /**
     * Bug #152: số điểm đổi voucher (`points_cost`) chỉ nhìn thấy được khi bấm
     * Edit — modal chi tiết không hề nhắc tới, nên người quản lý phải mở form
     * sửa (và chịu rủi ro lỡ tay Save) chỉ để xem một con số.
     *
     * LUÔN hiện, kể cả khi bằng 0 (chốt 28/08/2026).
     *
     * Bản trước ẩn hàng này khi `points_cost <= 0`, lý do: serializer của BE ép
     * `null` thành `0` (mục A6) nên `0` không phân biệt được với "không đổi bằng
     * điểm", và hiện "0" bị coi là khẳng định một điều FE không chắc. Thực tế
     * dùng cho thấy ẩn còn tệ hơn: người quản lý không biết hàng đó có tồn tại,
     * nên tưởng modal thiếu thông tin và vẫn phải mở Edit để kiểm — đúng cái
     * bug #152 muốn tránh. Hàng luôn có mặt thì "0" đọc là "không cần điểm",
     * còn thiếu dữ liệu thì hiện "—" như mọi field khác.
     */
    const rawPoints = p ? (p as { points_cost?: number | string | null }).points_cost : null;
    const pointsNum =
      rawPoints == null || rawPoints === '' ? Number.NaN : Number(rawPoints);
    const pointsVal = Number.isFinite(pointsNum) ? pointsNum.toLocaleString() : '';

    /**
     * Lượt dùng: "đã dùng / trần". Đây là con số DUY NHẤT cho biết voucher còn
     * hiệu lực thực tế hay đã cạn — quan trọng hơn cả ngày hết hạn, vì một mã
     * bị lan ra ngoài sẽ cạn trước hạn rất lâu.
     *
     * Trần rỗng thì hiện `∞` chứ không để trống: "không giới hạn" là một sự
     * thật cần đọc được, còn ô trống lại đọc thành "chưa có dữ liệu".
     *
     * Theo xác nhận của BE ngày 18/09/2026 (bug #251):
     *
     * 1. Trần là `total_quantity`. Field tên `usage_limit` trước đây không tồn
     *    tại ở serializer nào — FE gửi và đọc bằng tên đó nên trần luôn ra
     *    `undefined` và ô này hiện `∞` bất kể người dùng vừa đặt bao nhiêu.
     *    BE nay trả CẢ HAI tên trong response, đọc tên chuẩn trước.
     *
     * 2. `usage_count` đã được expose. Vẫn giữ nhánh `—` cho trường hợp thiếu:
     *    mặc định về `0` là khẳng định "chưa ai dùng mã này" trong khi thật ra
     *    không biết — với một voucher đang chạy thì đó là thông tin sai nguy
     *    hiểm hơn một dấu gạch.
     */
    const usageRecord = p as {
      usage_count?: number | string | null;
      total_quantity?: number | string | null;
      usage_limit?: number | string | null;
    } | null;
    const usedRaw = usageRecord?.usage_count;
    const usedNum = usedRaw == null || usedRaw === '' ? Number.NaN : Number(usedRaw);
    const limitRaw = usageRecord?.total_quantity ?? usageRecord?.usage_limit;
    const limitNum = limitRaw == null || limitRaw === '' ? Number.NaN : Number(limitRaw);
    const usageVal = `${Number.isFinite(usedNum) ? usedNum.toLocaleString() : '—'} / ${
      Number.isFinite(limitNum) ? limitNum.toLocaleString() : '∞'
    }`;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 font-manrope"
        onClick={onClose}
      >
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h3 className="text-lg font-semibold text-slate-900">{t('promotionDetail')}</h3>
            <button onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="border-t border-slate-100 px-6 py-5 overflow-y-auto overflow-x-hidden max-h-[70vh]">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : queryError ? (
              <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                <p className="font-semibold">Error Loading Promotion:</p>
                <p className="mt-1">{queryError instanceof Error ? queryError.message : 'Unknown API Error'}</p>
              </div>
            ) : !p ? (
              <p className="text-sm text-slate-500">{t('notFound')}</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('name')} value={nameVal} />
                  <Field label={t('code')} value={codeVal} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('type')} value={typeVal} />
                  <Field label={t('value')} value={valueVal} />
                </div>
                <Field label={t('conditions')} value={condVal} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('startAt')} value={startVal} />
                  <Field label={t('endAt')} value={endVal} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('promotionsStatus')} value={statusVal} />
                  {/* Bug #152 — luôn hiện, kể cả 0. Xem ghi chú ở `pointsVal`. */}
                  <Field label={t('pointsCost')} value={pointsVal} />
                </div>
                <Field label={t('usageCountLabel')} value={usageVal} />
                <TagList label={t('productIds')} items={productItems} emptyLabel={t('allProducts')} />
                <TagList label={t('categoryIds')} items={categoryItems} emptyLabel={t('allCategories')} />
                <TagList label={t('storeIds')} items={storeItems} emptyLabel={t('allStores')} />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  } catch (renderError: any) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 font-manrope"
        onClick={onClose}
      >
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 text-red-700">
          <h3 className="text-lg font-bold">Error Rendering Promotion Details</h3>
          <p className="mt-2 text-sm text-red-600 font-semibold">{renderError?.message || 'Unknown javascript crash'}</p>
          <div className="mt-4 flex justify-end">
            <button onClick={onClose} className="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700 hover:bg-slate-200">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
}
